import {
  VenueDetailsBySlugResponse,
  useAuth,
  useI18N,
  useSavedPlaces,
} from "common/hooks";
import { RWebShare } from "react-web-share";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDarkMode } from "@/hooks/useDarkMode";
import CreateList from "@/components/common/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/common/SavedPlaces/AddRemoveVenueToList";
import { cn } from "@/lib/utils";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import AddMapperNote from "./AddMapperNote";
import { useAtom } from "jotai";
import { actionToPerformAfterLoginAtom } from "@/store/atoms";
import { NotePencil } from "phosphor-react";
import SelectAddNoteType from "./SelectAddNoteType";
import AreaDetailsNotes from "./AreaDetailsNotes";
import AddVenueNote from "./AddVenueNote";
import FlagVenueNote from "@/features/flag-report/FlagVenueNote";
type VenueInfoButtonsProps = {
  venueDetailsReponse: VenueDetailsBySlugResponse;
};

function VenueInfoButtons({
  venueDetailsReponse,
}: VenueInfoButtonsProps) {
  const venueDetails = venueDetailsReponse.details;
  const [openSavedPlacesModal, setOpenSavedPlacesModal] = useState(false);
  const [addNoteTypeOpenModal, setAddNoteTypeOpenModal] = useState(false);
  const [addNoteOpenModal, setAddNoteOpenModal] = useState(false);
  const [addVenueNoteOpenModal, setAddVenueNoteOpenModal] = useState(false);
  const [editVenueNoteOpenModal, setEditVenueNoteOpenModal] = useState(false);
  const [flagVenueNoteOpenModal, setFlagVenueNoteOpenModal] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [flagNote, setFlagNote] = useState(null);
  const [areaDetailsOpenModal, setAreaDetailsOpenModal] = useState(false);

  const [savePlaceListMode, setSavePlaceListMode] = useState("");
  const isDark = useDarkMode();
  const { user, isLoggedIn } = useAuth();
  const i18n = useI18N();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom
  );

  const { showModal } = useAuthModals();
  const [listNameCreated, setListNameCreated] = useState("");
  const { isMapper } = useAuth();

  const handleCreateList = (listNameCreated) => {
    console.log("inside handleCreateList listNameCreated=", listNameCreated);
    setListNameCreated(listNameCreated);
  };

  const handleSavedPlacesModalClose = () => {
    console.log("Closing Modal handleSavedPlacesModalClose");
    setOpenSavedPlacesModal(false);
    if (savePlaceListMode === "create") {
      //refetch the lists and open the modal with create button  and all lists
      handleSaveVenue();
    } else {
      setListNameCreated("");
    }
  };

  const handleCreateNewClick = () => {
    console.log("handleCreateNewClick");
    setOpenSavedPlacesModal(false);
    setSavePlaceListMode("create");
    setOpenSavedPlacesModal(true);
  };

  const {
    refetch,
    isLoading: isSavedPlacesLoading,
    isFetching: isSavedPlacesFetching,
    data,
  } = useSavedPlaces();
  const savedPlaces = data?.saved_places || [];

  const venueTitle = useRef(null); // Reference for the venue name

  useEffect(() => {
    // console.log("venueDetails.businessSociabilitySaysApproved", venueDetails);

    console.log("check actionToPerformAfterLogin");
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      if (actionToPerformAfterLogin === "handleSaveVenue") {
        handleSaveVenue();
      }
      setActionToPerformAfterLogin("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  useEffect(() => {
    if (user) {
      refetch();
    }
  }, [refetch, user]);

  const handleSaveVenue = async () => {
    if (!user) {
      console.log("Please login");
      return;
    }

    const { data } = await refetch();
    if (!data) {
      return;
    }

    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  const isVenueSaved = useCallback(() => {
    if (isSavedPlacesFetching || isSavedPlacesLoading) {
      return false;
    }
    if (savedPlaces) {
      const flattened = savedPlaces
        ?.map((e) => e.saved_place_details.map((e) => e.business.id))
        .flat();
      const result = flattened.some((id) => id === venueDetails?.business.id);
      return result;
    }
    return false;
  }, [
    isSavedPlacesFetching,
    isSavedPlacesLoading,
    savedPlaces,
    venueDetails?.business.id,
  ]);

  useEffect(() => {
    if (!isSavedPlacesFetching && !isSavedPlacesLoading) {
      isVenueSaved();
    }
  }, [isSavedPlacesFetching, isSavedPlacesLoading, isVenueSaved]);

  // Focus on the paragraph when the component mounts
  useEffect(() => {
    if (venueTitle.current) {
      venueTitle.current.focus();
    }
  }, []);

  return (
    <div>
      <div className="flex gap-6" id="buttonsContainer">
        <button
          onClick={() => {
            if (isLoggedIn) {
              handleSaveVenue();
            } else {
              // Set the method in the Atom
              console.log("setActionToPerformAfterLogin in the Atom");
              setActionToPerformAfterLogin("handleSaveVenue");

              // Check if the current route matches the target route
              // if venue layer redirect to venue detail and then open login modal
              // if venue detail only then no redirect needed
              if (
                !matchRoute({
                  to: `/venuedetails/${venueDetails.business.slug}`,
                })
              ) {
                navigate({
                  to: `/venuedetails/${venueDetails.business.slug}`,
                  search: { source: "map", showAuthModal: true } as any,
                });
              }

              // Show login modal
              showModal(AuthModalScreen.EnterEmail);
            }
          }}
          className={cn(
            isVenueSaved()
              ? "text-white dark:text-primary-900 bg-primary-800 dark:bg-primary-300"
              : "text-blue-950 bg-primary-50",
            "px-4 py-2 rounded-[12px] border text-center flex items-center gap-2 button-small-semi-bold  border-primary-200 dark:border-white "
          )}
        >
          <img
            src={
              isDark
                ? isVenueSaved()
                  ? "/images/HeartSavedPlace.svg"
                  : "/images/HeartSavedPlace.svg"
                : isVenueSaved()
                  ? "/images/HeartSavedPlaceWhite.svg"
                  : "/images/HeartSavedPlace.svg"
            }
            alt=""
            aria-hidden="true"
          />
          {isVenueSaved() ? i18n.get("saved") : i18n.get("save")}
        </button>
        <RWebShare
          data={{
            text: i18n.get("seeThisSociabilityVenueText"),
            url:
              window.location.origin +
              `/venuedetails/${venueDetails.business.slug}`,
            title: i18n.get("seeThisSociabilityVenueTitle"),
          }}
          onClick={() => console.log("shared successfully!")}
        >
          <button className="px-4 py-2 rounded-[12px] border bg-primary-50 border-primary-200 dark:border-white text-center text-blue-950 flex items-center gap-2 button-small-semi-bold">
            <img src="/images/UploadSimple.svg" alt="" />
            {i18n.get("share")}
          </button>
        </RWebShare>
        {isMapper && (
          <button
            onClick={() => setAddNoteTypeOpenModal(true)}
            className={cn(
              "text-blue-950 bg-white",
              "px-4 py-2 rounded-[12px] border text-center flex items-center gap-2 button-small-semi-bold bg-primary-50 border-primary-200 dark:border-white "
            )}
          >
            <NotePencil size={24} />
            {i18n.get("note")}
          </button>
        )}
      </div>

      {openSavedPlacesModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openSavedPlacesModal}
          onClose={() => handleSavedPlacesModalClose()}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
            closeButton: "hidden",
          }}
        >
          {savePlaceListMode === "create" && (
            <CreateList
              onClose={() => handleSavedPlacesModalClose()}
              businessId={venueDetails?.business.id}
              venueDetails={venueDetails}
              onCreateList={(listNameCreated) =>
                handleCreateList(listNameCreated)
              }
            />
          )}

          {savePlaceListMode === "list" && (
            <AddRemoveVenueToList
              onClickCreateNew={() => handleCreateNewClick()}
              onClose={() => handleSavedPlacesModalClose()}
              businessId={venueDetails?.business.id}
              venueDetails={venueDetails}
              listNameCreated={listNameCreated}
            />
          )}
        </Modal>
      )}

      {addNoteTypeOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={addNoteTypeOpenModal}
          onClose={() => setAddNoteTypeOpenModal(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
            closeButton: "hidden",
          }}
        >
          <SelectAddNoteType
            onClose={() => {
              setAddNoteTypeOpenModal(false);
            }}
            onMapperNoteClick={() => {
              setAddNoteTypeOpenModal(false);
              setAddNoteOpenModal(true);
            }}
            onVenueNoteClick={() => {
              setAddNoteTypeOpenModal(false);
              setAddVenueNoteOpenModal(true);
            }}
          />
        </Modal>
      )}

      {areaDetailsOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={areaDetailsOpenModal}
          onClose={() => setAreaDetailsOpenModal(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
            closeButton: "hidden",
          }}
        >
          <AreaDetailsNotes
            venueDetailsReponse={venueDetailsReponse}
            mode={addNoteOpenModal ? "mappernote" : "venuenote"}
            onClose={() => {
              setAreaDetailsOpenModal(false);
            }}
          />
        </Modal>
      )}

      {addNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={addNoteOpenModal}
          onClose={() => setAddNoteOpenModal(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
            closeButton: "hidden",
          }}
        >
          <AddMapperNote
            venueDetailsReponse={venueDetailsReponse}
            onClose={() => {
              setAddNoteOpenModal(false);
            }}
            onInfoClick={() => {
              setAreaDetailsOpenModal(true);
            }}
          />
        </Modal>
      )}

      {addVenueNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={addVenueNoteOpenModal}
          onClose={() => setAddVenueNoteOpenModal(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
            closeButton: "hidden",
          }}
        >
          <AddVenueNote
            venueDetailsReponse={venueDetailsReponse}
            onClose={() => {
              setAddVenueNoteOpenModal(false);
            }}
            onInfoClick={() => {
              setAreaDetailsOpenModal(true);
            }}
          />
        </Modal>
      )}

      {editVenueNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={editVenueNoteOpenModal}
          onClose={() => setEditVenueNoteOpenModal(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
            closeButton: "hidden",
          }}
        >
          <AddVenueNote
            venueDetailsReponse={venueDetailsReponse}
            onClose={() => setEditVenueNoteOpenModal(false)}
            onInfoClick={() => {
              setAreaDetailsOpenModal(true);
            }}
            note={editNote}
          />
        </Modal>
      )}

      {flagVenueNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={flagVenueNoteOpenModal}
          onClose={() => setFlagVenueNoteOpenModal(false)}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
            closeButton: "hidden",
          }}
        >
          <FlagVenueNote
            venueDetails={venueDetails}
            onClose={() => setFlagVenueNoteOpenModal(false)}
            note={flagNote}
          />
        </Modal>
      )}
    </div>
  );
}

export default VenueInfoButtons;
